// src/app/shared/services/notification/notification.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, map, tap, forkJoin, mergeMap } from "rxjs";
import { environment } from '../../../../environments/environment';
import { PaginacaoResponse } from '../../../models/paginacaoResponse.interface';
import { SingleResponse } from '../../../models/rest.interface';
import { UserService } from "../user/user.service";
import { NotificationEntity, NotificationsApiResponse } from "./notification.model";
import { ToastService } from "../../toast/toast.service";

export interface NotificationResponse {
  id: number;
  attributes: {
    title: string;
    message: string;
    type: string;
    statuses: string;
    createdAt: string;
    // outros campos que você populou
  };
}

export interface NotificationListItem {
  id: number;
  title: string;
  message: string;
  type: 'web' | 'mobile' | 'sms' | 'email' | 'whatsapp';
  statuses: 'sent' | 'failed' | 'pending' | 'received';
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private BASE = `${environment.BACKEND_API}/notificacaos`;
  private unreadCount$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
    private readonly toast: ToastService,
  ) {
    this.BASE = `${environment.BACKEND_API}/notificacaos`;
  }

  /** Retorna um Observable com a quantidade de notificações não-lidas */
  getUnreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  listAll(): Observable<NotificationEntity[]> {
    return this.http
      .get<NotificationsApiResponse>(this.BASE)
      .pipe(
        // extrai somente o `data`
        map(response => response.data)
      );
  }

  listenNewNotifications() {
    const es = new EventSource(`${this.BASE}/stream`, { withCredentials: true });

    es.onmessage = (evt) => {
      const notif = JSON.parse(evt.data);
      // por exemplo, adiciona ao BehaviorSubject de notifications

      this.toast.notify({
        message: 'Chegou uma nova notificação!',
        type: 'INFO',
      });
      // atualiza contador
      this.unreadCount$.next(this.unreadCount$.value + 1);
    };

    es.onopen = (evt) =>  {
      console.log('Escutando sse')
      console.log(evt)
    }

    es.onerror = (evt) =>  {
      console.log('erro sse')
      console.log(evt)
    }
  }

  listNotificationsPaged(
    page: number = 1,
    pageSize: number = 10
  ): Observable<PaginacaoResponse<NotificationEntity>> {
    const params = new HttpParams()
      .set('pagination[withCount]', 'true')
      .set('pagination[page]', page.toString())
      .set('pagination[pageSize]', pageSize.toString())
      .set('sort', 'createdAt:desc')
      .set('filters[statuses][$eq]', 'sent')
      .set('filters[users_permissions_users][id][$eq]', this.getCurrentUserId().toString());
    return this.http
      .get<PaginacaoResponse<NotificationEntity>>(this.BASE, { params })
      .pipe(
        tap((res) => {
          // supondo que 'total' seja o total de notificações
          const unread = res.data.filter(n => n.statuses !== 'received').length;
          this.updateUnreadCount(unread);
        })
      );
  }

  /**
   * Busca uma notificação específica por ID (com populate caso necessário)
   */
  getNotificationById(id: number): Observable<SingleResponse<NotificationsApiResponse>> {
    const params = new HttpParams().set('populate', '*');
    return this.http.get<SingleResponse<NotificationsApiResponse>>(`${this.BASE}/${id}`, { params });
  }

  /**
   * Marca uma única notificação como 'received' (ou outro status de “lida”)
   */
  markAsRead(documentId: string): Observable<SingleResponse<NotificationsApiResponse>> {
    return this.http.put<SingleResponse<NotificationsApiResponse>>(
      `${this.BASE}/${documentId}`,
      { data: { statuses: 'received' } }
    ).pipe(
      tap(() => {
        // opcional: atualizar contador
        const current = this.unreadCount$.value;
        this.unreadCount$.next(Math.max(0, current - 1));
      })
    );
  }


  /**
   * Recebe um array de IDs e dispara vários markAsRead em paralelo.
   */
  markManyAsRead(ids: string[]): Observable<SingleResponse<NotificationsApiResponse>[]> {
    const calls = ids.map(id => this.markAsRead(id));
    return forkJoin(calls);
  }

  /**
   * Exemplo: marcar todas as notificações do usuário atual como lidas.
   */
  markAllAsRead(): Observable<SingleResponse<NotificationsApiResponse>[]> {
    return this.listNotificationsPaged(1, 9999).pipe(
      mergeMap(res => {
        const allIds = res.data.map(n => n.documentId);
        return this.markManyAsRead(allIds);
      })
    );
  }

  /**
   * Atualiza o contador de não-lidas (pode chamar após listagem ou via websocket)
   */
  updateUnreadCount(count: number) {
    this.unreadCount$.next(count);
  }

  /** Exemplo de como obter o ID do usuário (pegue do seu UserService) */
  private getCurrentUserId(): number {
    return  this.userService.user?.id ?? 0;
  }


}
